import React from 'react';
import {usePresentationEditedProps} from './use-presentation-edited-props';
import {useGraphSettings} from './use-graph-settings';
import {useLayoutAndCameraProps} from './use-layout-and-camera-props';
import {GraphPresentationDetails} from './interface';
import {useHiddenNodes} from './use-hidden-nodes';
import {getNodesFromRawNodes, parsePresDetailsLayout} from './helpers';
import {StructuredGraph} from '../../../../../components/graph/graph-logic/structured-graph';
import {useActionHistory} from './use-action-history';

interface Props {
    presentationEditedProps: ReturnType<typeof usePresentationEditedProps>;
    graphSettings: ReturnType<typeof useGraphSettings>;
    layoutAndCameraProps: ReturnType<typeof useLayoutAndCameraProps>;
    hiddenNodesProps: ReturnType<typeof useHiddenNodes>;
    actionHistoryProps: ReturnType<typeof useActionHistory>;
    structuredGraph?: StructuredGraph;
}

export function useUnsavedChangesActions(props: Props) {
    const {
        presentationEditedProps: {clearIsPresentationEdited, handlePresentationEdited},
        hiddenNodesProps: {clearHiddenNodes, setHiddenRawNodeIds, isHiddenNodesVisible},
        actionHistoryProps: {clearHistory},
        structuredGraph,
        graphSettings: {clearViewKind, setViewKind, clearMaxPathLength, setMaxPathLength},
        layoutAndCameraProps: {
            clearIsCustomLayout,
            setIsCustomLayout,
            clearLayoutAlgorithm,
            setLayoutAlgorithm,
            resetNodePositions,
            setVisibleNodesPositions,
            setInvisibleNodesPositions
        }
    } = props;

    // used to redraw graph on changes cleared or saved
    const [changesClearedOrSavedCounter, setChangesClearedOrSavedCounter] = React.useState(0);

    const clearUnsavedChanges = React.useCallback(() => {
        clearIsPresentationEdited();
        clearViewKind();
        clearMaxPathLength();
        clearIsCustomLayout();
        clearLayoutAlgorithm();
        resetNodePositions();
        clearHiddenNodes();
        clearHistory();

        setChangesClearedOrSavedCounter((val) => val + 1);
    }, [
        clearIsPresentationEdited,
        clearViewKind,
        clearMaxPathLength,
        clearIsCustomLayout,
        clearLayoutAlgorithm,
        resetNodePositions,
        clearHiddenNodes,
        clearHistory
    ]);

    const setUnsavedChanges = React.useCallback(
        (presentationDetails: GraphPresentationDetails) => {
            handlePresentationEdited();
            setViewKind(presentationDetails.viewKind);
            setMaxPathLength(String(presentationDetails.maxPathLength));
            setIsCustomLayout(String(presentationDetails.isLayoutCustom) as 'true' | 'false');
            setLayoutAlgorithm(presentationDetails.layoutAlgorithm);
            setHiddenRawNodeIds(presentationDetails.hiddenNodes);

            const hiddenNodeIds = getNodesFromRawNodes(
                presentationDetails.hiddenNodes,
                structuredGraph
            );
            const {visibleNodesPositions, invisibleNodesPositions} = parsePresDetailsLayout(
                presentationDetails.layout,
                isHiddenNodesVisible,
                (nodeId: number) => hiddenNodeIds.includes(nodeId)
            );
            setVisibleNodesPositions(visibleNodesPositions);
            setInvisibleNodesPositions(invisibleNodesPositions);
            clearHistory();
        },
        [
            handlePresentationEdited,
            setViewKind,
            setMaxPathLength,
            setIsCustomLayout,
            setLayoutAlgorithm,
            setVisibleNodesPositions,
            setInvisibleNodesPositions,
            isHiddenNodesVisible,
            setHiddenRawNodeIds,
            structuredGraph,
            clearHistory
        ]
    );

    return {
        clearUnsavedChanges,
        setUnsavedChanges,
        changesClearedOrSavedCounter: changesClearedOrSavedCounter
    };
}
